import { DateFormat, formatDateWithYear } from '@lyearn/core/utils/date/formatDate';
import { TFunction } from '@lyearn/i18n';
import { DripByEnum, DripSettingsType, DripTypeEnum } from '@/types/schema';

import { getLessonPageId } from '../../../utils/adapt';
import { getLessonInfo } from '../../../utils/courseActions';
import { ContentType } from '../../CourseBuilder/components/Sidebar/types';

export const dripByLabelMapping = {
  [DripByEnum.Section]: 'Course:Drip.DripBySectionDesc',
  [DripByEnum.Lesson]: 'Course:Drip.DripByLessonDesc',
} as Record<DripByEnum, string | undefined>;

export const getAdjacentLessons = (lessonPageId: string | null, lessons: ContentType[]) => {
  const currentLessonIndex = lessons.findIndex(
    (lesson) => getLessonPageId(lesson) === lessonPageId,
  );

  const prevLesson =
    currentLessonIndex === -1 ? undefined : getLessonInfo(lessons[currentLessonIndex - 1]);
  const nextLesson =
    currentLessonIndex === -1 ? undefined : getLessonInfo(lessons[currentLessonIndex + 1]);

  return { prevLesson, nextLesson, currentLesson: getLessonInfo(lessons?.[currentLessonIndex]) };
};

// Flattens a Tiptap/ProseMirror doc into block ids in document (reading) order.
export const getOrderedBlockIds = (content: any[] = []): string[] => {
  const ids: string[] = [];
  const walk = (nodes: any[]) => {
    for (const node of nodes) {
      if (node.attrs?.id) {
        ids.push(node.attrs.id);
      }
      if (node.content) {
        walk(node.content);
      }
    }
  };
  walk(content);
  return ids;
};

export const findBlockNodeById = (content: any[] = [], blockId: string): any | undefined => {
  for (const node of content) {
    if (node.attrs?.id === blockId) {
      return node;
    }
    if (node.content) {
      const found = findBlockNodeById(node.content, blockId);
      if (found) {
        return found;
      }
    }
  }
  return undefined;
};

export const getLockedLessonDesc = (
  t: TFunction,
  availableFrom?: string,
  dripSettings?: DripSettingsType | null,
) => {
  if (!dripSettings) {
    return '';
  }

  switch (dripSettings.type) {
    case DripTypeEnum.Interval: {
      const formattedDate = formatDateWithYear(availableFrom!, DateFormat.Date);
      return t('Course:Drip.IntervalLockedLessonDesc', { date: formattedDate });
    }
    case DripTypeEnum.Sequential: {
      const i18nKey = dripByLabelMapping[dripSettings.dripBy!];
      return i18nKey ? t(i18nKey) : '';
    }
    default:
      return '';
  }
};
